import { useGetStudy } from "@/api/studio/studies/queries";
import { Button } from "@/components/ui/button";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useModalStore } from "@/stores/use-modal-store";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";

/* Prop Types */
interface ExportPowerPointModalProps {
  projectId: string;
  studyId: string;
}

/*
 * EditStudyModal component
 *
 * This warns the user that editing the study will impact the reports and takes them to the study review page.
 */
export const EditStudyModal = ({ projectId, studyId }: ExportPowerPointModalProps) => {
  /* Hooks */
  const { close } = useModalStore((state) => state.modalActions);
  const [, navigate] = useLocation();
  const qc = useQueryClient();
  const isStudyEdit = useFeatureFlag("studyEdit1");

  /* Queries */
  const { data: study, isLoading: isStudyLoading } = useGetStudy(projectId, studyId);

  /* Handlers */
  const handleEditStudy = () => {
    // Clear all cache
    qc.clear();

    // Close the modal
    close();
    // Navigate to study-select (studyEdit1) or study-review (default)
    navigate(`/projects/${projectId}/studies/${studyId}/${study?.type}/${isStudyEdit ? "study-select" : "study-review"}`);
  };

  /* Render */
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2>Edit study</h2>
        <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
      </div>
      <p>
        {isStudyEdit ? "We will take you back to manage your assets." : "We will take you back to review your assets."}{" "}
        The changes you make will impact how scores are calculated:
      </p>
      <div>
        <ul className="flex pl-5 gap-4 flex-col py-2">
          {isStudyEdit && (
            <li>
              <ExclamationTriangleIcon className="size-4 inline-block mr-2 text-yellow-500" />
              Adding or removing assets
            </li>
          )}
          <li>
            <ExclamationTriangleIcon className="size-4 inline-block mr-2 text-yellow-500" />
            Adding or removing AOI elements
          </li>
          <li>
            <ExclamationTriangleIcon className="size-4 inline-block mr-2 text-yellow-500" />
            Changing priority alignment
          </li>
          <li>
            <ExclamationTriangleIcon className="size-4 inline-block mr-2 text-yellow-500" />
            Applying different context
          </li>
        </ul>
      </div>
      <div className="flex items-center justify-end">
        <Button variant="primary" onClick={handleEditStudy} disabled={isStudyLoading}>
          Edit study
        </Button>
      </div>
    </div>
  );
};
